import {
  CANVAS_BLOCK_SIZE,
  CANVAS_CELL_SIZE,
  CANVAS_SECTOR_SIZE,
  CANVAS_WORLD_MAX,
  CANVAS_WORLD_MIN,
  CANVAS_WORLD_SIZE,
} from '@blocksuite/store';

import { planEdgelessLineGrid } from './line-grid-plan.js';

let chromeId = 0;

export interface CanvasChromeViewport {
  width: number;
  height: number;
  zoom: number;
  translateX: number;
  translateY: number;
  left: number;
  top: number;
  pixelRatio: number;
}
export interface CanvasChromePalette {
  line: string;
  surface: string;
  label: string;
}

const escapeXml = (value: string) =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));
const smoothstep = (start: number, end: number, value: number) => {
  const t = clamp((value - start) / (end - start), 0, 1);
  return t * t * (3 - 2 * t);
};
const signed = (value: number) =>
  `${value < 0 ? '-' : '+'}${String(Math.abs(Math.round(value))).padStart(2, '0')}`;

export function planCanvasWorldChrome(view: CanvasChromeViewport) {
  const { width, height, zoom, translateX, translateY } = view;
  const worldLeft = translateX + CANVAS_WORLD_MIN * zoom;
  const worldTop = translateY + CANVAS_WORLD_MIN * zoom;
  const worldRight = translateX + CANVAS_WORLD_MAX * zoom;
  const worldBottom = translateY + CANVAS_WORLD_MAX * zoom;
  const materialScale = Math.min(1, zoom / 0.1);
  const thickness = 18 * materialScale;
  const left = Math.max(0, worldLeft),
    top = Math.max(0, worldTop);
  const right = Math.min(width, worldRight),
    bottom = Math.min(height, worldBottom);
  return {
    worldLeft,
    worldTop,
    worldRight,
    worldBottom,
    left,
    top,
    right,
    bottom,
    materialScale,
    thickness,
    rulerX: clamp(
      worldLeft - thickness,
      0,
      Math.max(0, worldRight - thickness)
    ),
    rulerY: clamp(
      worldTop - thickness,
      0,
      Math.max(0, worldBottom - thickness)
    ),
    visible: right > left && bottom > top,
  };
}

/** Screen-space SVG chrome stays below document content and never intercepts input. */
export class CanvasWorldChrome {
  private readonly clipId = `edgeless-world-axes-${++chromeId}`;
  private readonly graphic: SVGSVGElement;
  private readonly cursor: SVGSVGElement;
  private view: CanvasChromeViewport | null = null;
  private palette: CanvasChromePalette | null = null;
  private pointer: { x: number; y: number } | null = null;

  constructor(host: HTMLElement) {
    const create = () => {
      const svg = host.ownerDocument.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg'
      );
      svg.setAttribute('aria-hidden', 'true');
      svg.style.cssText =
        'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;overflow:hidden';
      host.append(svg);
      return svg;
    };
    this.graphic = create();
    this.cursor = create();
  }

  update(
    view: CanvasChromeViewport,
    palette: CanvasChromePalette,
    title: string
  ) {
    if (
      !Object.values(view).every(Number.isFinite) ||
      view.width <= 0 ||
      view.height <= 0 ||
      view.zoom <= 0
    )
      return;
    this.view = view;
    this.palette = palette;
    const { width, height, zoom, translateX: tx, translateY: ty } = view;
    const p = planCanvasWorldChrome(view);
    const color = escapeXml(palette.line),
      labelColor = escapeXml(palette.label);
    const surface = escapeXml(palette.surface);
    const dpr = Math.max(0.1, view.pixelRatio);
    const stroke = Math.max(p.materialScale, 1 / dpr);
    const rect = (x: number, y: number, w: number, h: number, fill: string) =>
      `<rect x="${x}" y="${y}" width="${Math.max(0, w)}" height="${Math.max(0, h)}" fill="${fill}"/>`;
    const text = (
      x: number,
      y: number,
      content: string,
      size: number,
      rotation = 0,
      weight = 600
    ) =>
      `<text transform="translate(${x} ${y}) rotate(${rotation})" dominant-baseline="hanging" font-family="ui-monospace, monospace" font-size="${size}" font-weight="${weight}" fill="${labelColor}">${escapeXml(content)}</text>`;
    const chunks: string[] = [];
    // A distinct outside material without masking or moving existing content.
    chunks.push(
      `<path fill="rgba(0,0,0,0.14)" fill-rule="evenodd" d="M0 0H${width}V${height}H0Z M${p.worldLeft} ${p.worldTop}H${p.worldRight}V${p.worldBottom}H${p.worldLeft}Z"/>`
    );
    const normalizedTitle = title.trim();
    if (normalizedTitle) {
      const fontSize =
        clamp(
          CANVAS_WORLD_SIZE / Math.max(12, [...normalizedTitle].length * 0.62),
          360,
          1800
        ) * zoom;
      const y = ty + (CANVAS_WORLD_MIN - CANVAS_SECTOR_SIZE * 2) * zoom;
      if (y >= 0 && y - fontSize <= height) {
        chunks.push(
          `<text x="${tx}" y="${y}" text-anchor="middle" font-family="Inter,system-ui,sans-serif" font-size="${fontSize}" font-weight="700" letter-spacing="${fontSize / 60}" fill="${color}" opacity="0.78">${escapeXml(normalizedTitle)}</text>`
        );
      }
    }
    if (p.visible) {
      const clip = rect(
        p.left,
        p.top,
        p.right - p.left,
        p.bottom - p.top,
        'white'
      );
      chunks.push(
        `<defs><clipPath id="${this.clipId}">${clip}</clipPath></defs>`
      );
      const layers = planEdgelessLineGrid(zoom);
      const detail = Math.max(layers[0].opacity, layers[1].opacity);
      const axisWidth = 1.35 + smoothstep(0.08, 0.1, zoom) * (2.75 - 1.35);
      const pixelWidth = Math.ceil(axisWidth * dpr);
      const axisX =
        Math.round((view.left + tx) * dpr - pixelWidth / 2) / dpr - view.left;
      const axisY =
        Math.round((view.top + ty) * dpr - pixelWidth / 2) / dpr - view.top;
      const axes =
        rect(axisX, p.top, pixelWidth / dpr, p.bottom - p.top, color) +
        rect(p.left, axisY, p.right - p.left, pixelWidth / dpr, color);
      chunks.push(
        `<g clip-path="url(#${this.clipId})" opacity="${((0.68 + detail * 0.22) * axisWidth * dpr) / pixelWidth}">${axes}</g>`
      );
      let axisTicks = '';
      const xs =
        Math.ceil(
          Math.max(CANVAS_WORLD_MIN, (p.left - tx) / zoom) / CANVAS_SECTOR_SIZE
        ) * CANVAS_SECTOR_SIZE;
      const ys =
        Math.ceil(
          Math.max(CANVAS_WORLD_MIN, (p.top - ty) / zoom) / CANVAS_SECTOR_SIZE
        ) * CANVAS_SECTOR_SIZE;
      for (
        let x = xs;
        x <= Math.min(CANVAS_WORLD_MAX, (p.right - tx) / zoom);
        x += CANVAS_SECTOR_SIZE
      ) {
        axisTicks += `M${tx + x * zoom} ${ty - 3.5}v7`;
      }
      for (
        let y = ys;
        y <= Math.min(CANVAS_WORLD_MAX, (p.bottom - ty) / zoom);
        y += CANVAS_SECTOR_SIZE
      ) {
        axisTicks += `M${tx - 3.5} ${ty + y * zoom}h7`;
      }
      chunks.push(
        `<path d="${axisTicks}" clip-path="url(#${this.clipId})" stroke="${color}" stroke-width="${axisWidth}" opacity="${0.58 + detail * 0.19}"/>`
      );
      chunks.push(
        `<rect x="${p.worldLeft}" y="${p.worldTop}" width="${CANVAS_WORLD_SIZE * zoom}" height="${CANVAS_WORLD_SIZE * zoom}" fill="none" stroke="${color}" stroke-width="${stroke}" opacity="0.72"/>`
      );
      chunks.push(
        rect(p.rulerX, p.rulerY, p.right - p.rulerX, p.thickness, surface),
        rect(p.rulerX, p.rulerY, p.thickness, p.bottom - p.rulerY, surface)
      );
      const interval =
        CANVAS_CELL_SIZE * zoom >= 6
          ? CANVAS_CELL_SIZE
          : CANVAS_BLOCK_SIZE * zoom >= 6
            ? CANVAS_BLOCK_SIZE
            : CANVAS_SECTOR_SIZE;
      let ticks = '';
      const labels: string[] = [];
      const stride = Math.max(
        1,
        Math.ceil((52 * p.materialScale) / (CANVAS_SECTOR_SIZE * zoom))
      );
      for (const axis of ['x', 'y'] as const) {
        const offset = axis === 'x' ? tx : ty;
        const start = Math.max(
          CANVAS_WORLD_MIN,
          ((axis === 'x' ? p.left : p.top) - offset) / zoom
        );
        const end = Math.min(
          CANVAS_WORLD_MAX,
          ((axis === 'x' ? p.right : p.bottom) - offset) / zoom
        );
        for (
          let value = Math.ceil(start / interval) * interval;
          value <= end;
          value += interval
        ) {
          const position = offset + value * zoom;
          const length =
            value % CANVAS_SECTOR_SIZE === 0
              ? p.thickness
              : value % CANVAS_BLOCK_SIZE === 0
                ? (p.thickness * 2) / 3
                : p.thickness / 3;
          ticks +=
            axis === 'x'
              ? `M${position} ${p.rulerY + p.thickness - length}v${length}`
              : `M${p.rulerX + p.thickness - length} ${position}h${length}`;
        }
        const step = CANVAS_SECTOR_SIZE * stride;
        for (
          let value = Math.ceil(start / step) * step;
          value <= end;
          value += step
        ) {
          const position = offset + value * zoom;
          const size = (value === 0 ? 11 : 9) * p.materialScale;
          if (
            axis === 'y' &&
            value === 0 &&
            tx >= p.left &&
            tx <= p.right &&
            ty >= p.top &&
            ty <= p.bottom
          )
            continue;
          labels.push(
            axis === 'x'
              ? text(
                  position + 3 * p.materialScale,
                  p.rulerY + p.materialScale,
                  signed(value / CANVAS_SECTOR_SIZE),
                  size,
                  0,
                  value === 0 ? 700 : 600
                )
              : text(
                  p.rulerX + p.materialScale,
                  position - 3 * p.materialScale,
                  signed(-value / CANVAS_SECTOR_SIZE),
                  size,
                  -90,
                  value === 0 ? 700 : 600
                )
          );
        }
      }
      chunks.push(
        `<path d="${ticks}" stroke="${color}" stroke-width="${stroke}" opacity="0.68"/>`,
        ...labels
      );
      if (tx >= p.left && tx <= p.right && ty >= p.top && ty <= p.bottom) {
        const s = p.materialScale,
          outsideFits = tx >= 48 * s;
        const sunX = tx + (outsideFits ? -38 : 9) * s;
        const labelX = tx + (outsideFits ? -25 : 19) * s;
        let sun = `M${sunX - 4 * s} ${ty}a${4 * s} ${4 * s} 0 0 1 ${8 * s} 0M${sunX - 6 * s} ${ty}h${12 * s}`;
        for (let index = 0; index <= 4; index++) {
          const angle = Math.PI + (index * Math.PI) / 4;
          sun += `M${sunX + Math.cos(angle) * 5.5 * s} ${ty + Math.sin(angle) * 5.5 * s}L${sunX + Math.cos(angle) * 7.5 * s} ${ty + Math.sin(angle) * 7.5 * s}`;
        }
        chunks.push(
          `<path d="${sun}" fill="none" stroke="${labelColor}" stroke-width="${Math.max(stroke, 1.25 * s)}" opacity="0.96"/>`,
          text(labelX, ty - 11 * s * 0.62, '+00', 11 * s, 0, 700)
        );
      }
      chunks.push(
        rect(
          p.rulerX - stroke,
          p.rulerY - stroke,
          p.thickness + stroke * 2,
          p.thickness + stroke * 2,
          surface
        )
      );
      chunks.push(
        `<path d="M${p.rulerX + p.thickness} ${p.top}V${p.bottom}M${p.left} ${p.rulerY + p.thickness}H${p.right}" stroke="${color}" stroke-width="${stroke}" opacity="0.72"/>`
      );
    }
    this.graphic.setAttribute('viewBox', `0 0 ${width} ${height}`);
    this.cursor.setAttribute('viewBox', `0 0 ${width} ${height}`);
    this.graphic.innerHTML = chunks.join('');
    this.updatePointer(this.pointer);
  }

  updatePointer(pointer: { x: number; y: number } | null) {
    this.pointer = pointer;
    if (!this.view || !this.palette) return;
    const p = planCanvasWorldChrome(this.view);
    if (
      !pointer ||
      !p.visible ||
      pointer.x < p.left ||
      pointer.x > p.right ||
      pointer.y < p.top ||
      pointer.y > p.bottom
    ) {
      this.cursor.replaceChildren();
      return;
    }
    this.cursor.innerHTML = `<path d="M${pointer.x} ${p.rulerY}v${p.thickness}M${p.rulerX} ${pointer.y}h${p.thickness}" stroke="${escapeXml(this.palette.line)}" stroke-width="${Math.max(1 / this.view.pixelRatio, 1.25 * p.materialScale)}" stroke-dasharray="${2.5 * p.materialScale}"/>`;
  }

  dispose() {
    this.graphic.remove();
    this.cursor.remove();
  }
}
